import { zodResolver } from '@hookform/resolvers/zod';
import { isValidItalianVatNumber, normalizeItalianVatNumber } from '@console/shared-kernel';
import { useMemo, useState, type ChangeEvent } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { ConfirmDialog } from '@/communications/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FormFeedback } from '@/components/ui/form-feedback';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/expenses/capture/NativeSelect';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/lib/api-types';
import { usePendingWrite } from '@/lib/use-pending-write';
import { countryOptions } from './countries';
import { taxCodeProblem } from './tax-code';

export type OwnerDetails = components['schemas']['OwnerDetailsResponse'];

const ITALY = 'IT';

type ErrorKey =
  | 'required'
  | 'tooLong'
  | 'taxCodeLength'
  | 'taxCodeShape'
  | 'taxCodeCheck'
  | 'taxCodeForeign'
  | 'taxCodeInvalid'
  | 'postalCode'
  | 'province'
  | 'vatNumberLength'
  | 'vatNumberCheck'
  | 'vatNumberInvalid';

/** A tax id from abroad, as core-api takes it: letters, digits, dots, slashes or dashes. */
const FOREIGN_TAX_ID = /^[A-Z0-9./-]{2,32}$/;

/** As core-api keeps it: no spaces, upper case. */
const compactTaxCode = (value: string) => value.replace(/\s/g, '').toUpperCase();

/** The fields as `PATCH /v1/profile` and Gestione's `PUT` take them. */
export type OwnerDetailsInput = OwnerDetails;

/** What a write answered: the details as saved, or the refusal's status and problem code. */
export type OwnerDetailsWriteOutcome =
  | { readonly ok: true; readonly details: OwnerDetails | null }
  | { readonly ok: false; readonly status: number | undefined; readonly code: string | null };

/** A write that never rejects: a network failure or a failed token refresh is a failure too. */
async function attempt(
  write: (next: OwnerDetailsInput | null) => Promise<OwnerDetailsWriteOutcome>,
  next: OwnerDetailsInput | null,
): Promise<OwnerDetailsWriteOutcome> {
  try {
    return await write(next);
  } catch {
    return { ok: false, status: undefined, code: null };
  }
}

/** The person's own write, through their profile. */
async function writeOwnProfile(next: OwnerDetailsInput | null): Promise<OwnerDetailsWriteOutcome> {
  const { data, error, response } = await apiClient.PATCH('/v1/profile', {
    body: { ownerDetails: next },
  });
  if (error !== undefined || !response.ok || data === undefined) {
    return { ok: false, status: response.status, code: error?.code ?? null };
  }
  return { ok: true, details: data.ownerDetails };
}

// The messages are i18n keys under `profile:ownerDetails.errors`; the form translates them.
const text = (max: number) => z.string().trim().min(1, 'required').max(max, 'tooLong');

const schema = z
  .object({
    taxCode: z.string(),
    street: text(255),
    postalCode: text(16),
    city: text(120),
    province: z.string().trim().max(120, 'tooLong'),
    country: z.string(),
    vatNumber: z.string(),
  })
  .superRefine((values, context) => {
    const taxCode = compactTaxCode(values.taxCode);
    if (taxCode === '') {
      context.addIssue({ code: 'custom', path: ['taxCode'], message: 'required' });
    } else if (values.country !== ITALY) {
      // Decision 192: a tax id from abroad, without the Italian checks.
      if (!FOREIGN_TAX_ID.test(taxCode)) {
        context.addIssue({ code: 'custom', path: ['taxCode'], message: 'taxCodeForeign' });
      }
    } else {
      const problem = taxCodeProblem(taxCode);
      if (problem !== null) {
        const key = { length: 'taxCodeLength', shape: 'taxCodeShape', check: 'taxCodeCheck' };
        context.addIssue({ code: 'custom', path: ['taxCode'], message: key[problem] });
      }
    }
    // #864: optional, and Italian whatever the address: eleven digits, the last one checking the rest.
    const vatNumber = normalizeItalianVatNumber(values.vatNumber);
    if (vatNumber !== '' && !/^\d{11}$/.test(vatNumber)) {
      context.addIssue({ code: 'custom', path: ['vatNumber'], message: 'vatNumberLength' });
    } else if (vatNumber !== '' && !isValidItalianVatNumber(vatNumber)) {
      context.addIssue({ code: 'custom', path: ['vatNumber'], message: 'vatNumberCheck' });
    }
    if (values.country !== ITALY) {
      return;
    }
    if (values.postalCode.trim() !== '' && !/^\d{5}$/.test(values.postalCode.trim())) {
      context.addIssue({ code: 'custom', path: ['postalCode'], message: 'postalCode' });
    }
    if (!/^[A-Za-z]{2}$/.test(values.province.trim())) {
      context.addIssue({ code: 'custom', path: ['province'], message: 'province' });
    }
  });

type Values = z.infer<typeof schema>;
type Field = keyof Values;

const EMPTY: Values = {
  taxCode: '',
  street: '',
  postalCode: '',
  city: '',
  province: '',
  country: ITALY,
  vatNumber: '',
};

function toValues(details: OwnerDetails | null | undefined): Values {
  if (details === null || details === undefined) {
    return EMPTY;
  }
  return {
    taxCode: details.taxCode,
    street: details.street,
    postalCode: details.postalCode,
    city: details.city,
    province: details.province ?? '',
    country: details.country,
    vatNumber: details.vatNumber ?? '',
  };
}

/** Upper-cases the field as it is typed, keeping the caret where it was. */
function upperCaseInPlace(event: ChangeEvent<HTMLInputElement>): void {
  const input = event.target;
  const upper = input.value.toUpperCase();
  if (upper !== input.value) {
    const { selectionStart, selectionEnd } = input;
    input.value = upper;
    input.setSelectionRange(selectionStart, selectionEnd);
  }
}

/**
 * «Dati per la nota spese» (R-c, #833): the caller's own tax code, VAT number (optional, #864)
 * and address, which Note spese prints on the note's PDF. Self-only like the rest of the page:
 * `PATCH /v1/profile` takes no target user. The tax code is shown in full only here, to its owner. Errors show on the field as
 * the person types (U10; the server's `TAX_CODE_INVALID` and 400s in words), «Salva» stays
 * disabled with its reason while nothing changed (U11), a save or a deletion answers with a short
 * confirmation (U9), and «Cancella i dati» asks first.
 *
 * Decision 192: with an address abroad the tax code is a tax id from abroad, and Gestione fills
 * in someone else's details with the same form — `write` and `title` say whose.
 */
export function OwnerDetailsSection({
  ownerDetails,
  onSaved,
  write = writeOwnProfile,
  title,
  clearConfirmation,
  framed = true,
}: {
  ownerDetails: OwnerDetails | null;
  onSaved: (details: OwnerDetails | null) => void;
  write?: (next: OwnerDetailsInput | null) => Promise<OwnerDetailsWriteOutcome>;
  title?: string;
  /** Whose data «Cancella i dati» removes, when it is not the caller's own (decision 192). */
  clearConfirmation?: { readonly title: string; readonly ask: string };
  /** `false` inside a container that already frames it (Gestione's side panel): no card border. */
  framed?: boolean;
}) {
  const { t, i18n } = useTranslation(['profile', 'common']);
  const [apiError, setApiError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const { pending, run } = usePendingWrite();
  const countries = useMemo(() => countryOptions(i18n.language), [i18n.language]);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    watch,
    formState: { errors, isDirty },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: toValues(ownerDetails),
    mode: 'onTouched',
  });
  const inItaly = watch('country') === ITALY;

  const errorText = (field: Field) => {
    const message = errors[field]?.message;
    return message === undefined
      ? undefined
      : t(`ownerDetails.errors.${message as ErrorKey}`, { defaultValue: message });
  };

  const afterWrite = (details: OwnerDetails | null, message: string) => {
    reset(toValues(details));
    setConfirmation(message);
    onSaved(details);
    toast.success(message);
  };

  const save = async (values: Values) => {
    // Enter submits the form even while «Salva» is disabled: nothing changed, nothing to send.
    if (!isDirty) return;
    setApiError(null);
    setConfirmation(null);
    const province = values.province.trim();
    const vatNumber = normalizeItalianVatNumber(values.vatNumber);
    const outcome = await attempt(write, {
      taxCode: compactTaxCode(values.taxCode),
      street: values.street.trim(),
      postalCode: values.postalCode.trim(),
      city: values.city.trim(),
      province:
        province === '' ? null : values.country === ITALY ? province.toUpperCase() : province,
      country: values.country,
      vatNumber: vatNumber === '' ? null : vatNumber,
    });
    if (!outcome.ok) {
      if (outcome.code === 'TAX_CODE_INVALID') {
        setError('taxCode', { message: 'taxCodeInvalid' }, { shouldFocus: true });
        setApiError(t('ownerDetails.errors.taxCodeInvalid'));
        return;
      }
      if (outcome.code === 'VAT_NUMBER_INVALID') {
        setError('vatNumber', { message: 'vatNumberInvalid' }, { shouldFocus: true });
        setApiError(t('ownerDetails.errors.vatNumberInvalid'));
        return;
      }
      setApiError(
        outcome.status === 400 ? t('ownerDetails.errors.invalid') : t('ownerDetails.errors.failed'),
      );
      return;
    }
    afterWrite(outcome.details, t('ownerDetails.saved'));
  };

  const clear = async () => {
    setApiError(null);
    setConfirmation(null);
    const outcome = await attempt(write, null);
    // The confirmation closes either way, so a failure shows under the form, where «Cancella i
    // dati» can be tried again; the values and the panel around the form stay as they are.
    setAsking(false);
    if (!outcome.ok) {
      setApiError(t('ownerDetails.errors.failed'));
      return;
    }
    afterWrite(null, t('ownerDetails.cleared'));
  };

  const field = (name: Field) => ({
    id: `owner-details-${name}`,
    'aria-invalid': errors[name] !== undefined,
    'aria-describedby': errors[name] !== undefined ? `owner-details-${name}-error` : undefined,
  });

  const fieldError = (name: Field) =>
    errors[name] !== undefined ? (
      <p id={`owner-details-${name}-error`} className="text-destructive text-sm">
        {errorText(name)}
      </p>
    ) : null;

  const taxCode = register('taxCode');

  const heading = (
    <>
      <CardTitle id="owner-details-title" className="card-label">
        {title ?? t('ownerDetails.title')}
      </CardTitle>
      <p className="text-muted-foreground text-sm">{t('ownerDetails.why')}</p>
    </>
  );
  const clearDialog = (
    <>
      {asking && (
        <ConfirmDialog
          title={clearConfirmation?.title ?? t('ownerDetails.clearTitle')}
          ask={clearConfirmation?.ask ?? t('ownerDetails.clearAsk')}
          confirmLabel={t('ownerDetails.clear')}
          destructive
          busy={pending}
          onConfirm={() => void run(clear)}
          onClose={() => setAsking(false)}
        />
      )}
    </>
  );
  const form = (
    <form
      className="space-y-5"
      aria-labelledby="owner-details-title"
      noValidate
      onSubmit={(event) => void handleSubmit((values) => run(() => save(values)))(event)}
      onChange={() => setConfirmation(null)}
    >
      <div className="max-w-xs space-y-2">
        <label className="field-label" htmlFor="owner-details-taxCode">
          {t('ownerDetails.taxCode')}
        </label>
        <Input
          {...field('taxCode')}
          {...taxCode}
          onChange={(event) => {
            upperCaseInPlace(event);
            void taxCode.onChange(event);
          }}
          className="min-h-11 font-mono tracking-wide"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          maxLength={inItaly ? 16 : 32}
        />
        {!inItaly && (
          <p className="text-muted-foreground text-xs">{t('ownerDetails.taxCodeForeignHint')}</p>
        )}
        {fieldError('taxCode')}
      </div>

      <div className="max-w-xs space-y-2">
        <label className="field-label" htmlFor="owner-details-vatNumber">
          {t('ownerDetails.vatNumber')}
        </label>
        <Input
          {...field('vatNumber')}
          {...register('vatNumber')}
          aria-describedby={
            errors.vatNumber !== undefined
              ? 'owner-details-vatNumber-hint owner-details-vatNumber-error'
              : 'owner-details-vatNumber-hint'
          }
          className="min-h-11 font-mono tracking-wide"
          autoComplete="off"
          inputMode="numeric"
          spellCheck={false}
          maxLength={20}
        />
        <p id="owner-details-vatNumber-hint" className="text-muted-foreground text-xs">
          {t('ownerDetails.vatNumberHint')}
        </p>
        {fieldError('vatNumber')}
      </div>

      <fieldset className="@container space-y-4">
        <legend className="field-label mb-2">{t('ownerDetails.address')}</legend>
        <div className="space-y-2">
          <label className="field-label" htmlFor="owner-details-street">
            {t('ownerDetails.street')}
          </label>
          <Input
            {...field('street')}
            {...register('street')}
            className="min-h-11"
            autoComplete="street-address"
          />
          {fieldError('street')}
        </div>
        {/* By the form's own width, not the viewport's: a side panel is narrow on a wide screen. */}
        <div className="grid gap-4 @xl:grid-cols-[minmax(0,10rem)_minmax(0,1fr)_minmax(0,10rem)]">
          <div className="space-y-2">
            <label className="field-label" htmlFor="owner-details-postalCode">
              {t('ownerDetails.postalCode')}
            </label>
            <Input
              {...field('postalCode')}
              {...register('postalCode')}
              className="min-h-11"
              autoComplete="postal-code"
              inputMode={inItaly ? 'numeric' : 'text'}
            />
            {fieldError('postalCode')}
          </div>
          <div className="space-y-2">
            <label className="field-label" htmlFor="owner-details-city">
              {t('ownerDetails.city')}
            </label>
            <Input
              {...field('city')}
              {...register('city')}
              className="min-h-11"
              autoComplete="address-level2"
            />
            {fieldError('city')}
          </div>
          <div className="space-y-2">
            <label className="field-label" htmlFor="owner-details-province">
              {t('ownerDetails.province')}
            </label>
            <Input
              {...field('province')}
              {...register('province')}
              className="min-h-11"
              autoComplete="address-level1"
              autoCapitalize={inItaly ? 'characters' : 'words'}
              maxLength={inItaly ? 2 : 120}
            />
            {fieldError('province')}
          </div>
        </div>
        <div className="max-w-sm space-y-2">
          <label className="field-label" htmlFor="owner-details-country">
            {t('ownerDetails.country')}
          </label>
          <NativeSelect
            id="owner-details-country"
            className="min-h-11"
            autoComplete="country"
            {...register('country')}
          >
            {countries.map((country) => (
              <option key={country.code} value={country.code}>
                {country.name}
              </option>
            ))}
          </NativeSelect>
        </div>
      </fieldset>

      {apiError !== null && <FormFeedback tone="error">{apiError}</FormFeedback>}
      {confirmation !== null && <FormFeedback tone="success">{confirmation}</FormFeedback>}

      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="submit"
          className="min-h-11"
          disabled={!isDirty || pending}
          aria-describedby={!isDirty ? 'owner-details-unchanged' : undefined}
        >
          {t('common:save')}
        </Button>
        {!isDirty && (
          <span id="owner-details-unchanged" className="text-muted-foreground text-sm">
            {t('ownerDetails.unchanged')}
          </span>
        )}
        {ownerDetails !== null && (
          <Button
            type="button"
            variant="outline"
            className="text-destructive min-h-11 sm:ml-auto"
            disabled={pending}
            onClick={() => setAsking(true)}
          >
            {t('ownerDetails.clear')}
          </Button>
        )}
      </div>
    </form>
  );

  if (!framed) {
    // Gestione's side panel already frames the form and names the person: no card inside it.
    return (
      <section aria-labelledby="owner-details-title" className="space-y-4">
        <div className="space-y-1.5">{heading}</div>
        {form}
        {clearDialog}
      </section>
    );
  }
  return (
    <Card>
      <CardHeader>{heading}</CardHeader>
      <CardContent>{form}</CardContent>
      {clearDialog}
    </Card>
  );
}
